'use client';

import React, { useId } from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { classnames } from '@/utils/classnames/classnames';
import { SortByProps } from './types';

// EWM-185 sort-by dropdown.
//
// Sits to the right of the filter toolbar (next to ResultsCount).
// Single-select dropdown — clicking an option closes the popover and
// fires onChange with the new value. Trigger label echoes the
// currently-selected option so the user always sees current state.
//
// Pure presentational: caller owns the value and wires onChange to
// URL refinement. Layout / colours match the v0 mock.
const SortBy = ({ options, value, onChange, className = '' }: SortByProps) => {
  const listboxId = useId();
  const current = options.find((o) => o.value === value) ?? options[0];

  // Screen-reader label includes the current selection so users hear
  // what's active before opening the popover. Headless UI handles
  // aria-expanded automatically.
  const triggerLabel = current ? `Sort by, currently ${current.label}` : 'Sort by';

  return (
    <Popover className={classnames('relative inline-block', className)}>
      {({ open, close }) => (
        <>
          <PopoverButton
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-label={triggerLabel}
            className={classnames(
              'text-13 inline-flex items-center gap-1.5 rounded-full border bg-white px-4 py-2 text-gray-700 transition-colors',
              // EWM-271 — Dan v0 pulses the trigger border to primary
              // when the popover is open (matches FilterPill's
              // `open OR active` pattern). For Sort there's no
              // selection state distinct from the default, so the
              // border switch is purely tied to the open state.
              open ? 'border-primary' : 'border-gray-300 hover:border-gray-400',
              'focus:ring-primary/40 focus:ring-2 focus:outline-hidden',
            )}
          >
            <span aria-hidden>
              Sort By <strong>{current?.label}</strong>
            </span>
            <ChevronDownIcon className="size-4" aria-hidden />
          </PopoverButton>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel className="absolute top-full right-0 z-20 mt-1 w-52 rounded-lg border border-gray-200 bg-white shadow-lg">
              <ul id={listboxId} role="listbox" aria-label="Sort by">
                {options.map((option) => {
                  const selected = option.value === value;
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          onChange?.(option.value);
                          close();
                        }}
                        className={classnames(
                          'text-13 block w-full px-4 py-2.5 text-left transition-colors hover:bg-gray-50',
                          selected ? 'text-primary font-semibold' : 'text-gray-700',
                        )}
                      >
                        {option.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default SortBy;
